import { useEffect, useState } from "react";
import { FileDiff, RefreshCw, ShieldCheck } from "lucide-react";
import { Alert, Button, EmptyState } from "@/components/ui";
import { useContextApi, isAbortError, type Proposal, type ProposalStatus } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { ProposalDetail } from "./ProposalDetail";
import { formatDateTime, StatusBadge, useErrorHandler } from "./shared";

type Filter = "all" | ProposalStatus;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "ready", label: "Klaar voor review" },
  { value: "all", label: "Alle" },
  { value: "draft", label: "Concept" },
  { value: "checking", label: "Wordt gecontroleerd" },
  { value: "published", label: "Gepubliceerd" },
  { value: "rejected", label: "Afgewezen" },
];

function CheckDelta({ p }: { p: Proposal }) {
  if (!p.check) return null;
  const d = (p.check.after?.mean ?? NaN) - (p.check.before?.mean ?? NaN);
  if (!Number.isFinite(d)) return null;
  return (
    <span className={cn("font-mono text-xs font-medium tabular-nums", d > 0 ? "text-success" : d < 0 ? "text-destructive" : "text-muted-foreground")} title="Verschil in gemiddelde score">
      score {d > 0 ? "+" : d < 0 ? "−" : "±"}
      {Math.abs(d).toFixed(2)}
    </span>
  );
}

export function ProposalsView({
  refreshKey,
  openId,
  onOpen,
  onChanged,
  liveVersion,
  onOpenTable,
}: {
  /** Bump to reload the list (e.g. after the chat created a proposal). */
  refreshKey: number;
  openId: string | null;
  onOpen: (id: string | null) => void;
  /** Called after publish/reject so the page can reload versions. */
  onChanged: () => void;
  liveVersion?: string;
  onOpenTable: (fqn: string) => void;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [filter, setFilter] = useState<Filter>("all");
  const [items, setItems] = useState<Proposal[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listProposals(undefined, ac.signal)
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(handleError(e));
        setItems(null);
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, handleError, refreshKey, reload]);

  // Desktop: open the first proposal awaiting review so pending work is visible immediately.
  useEffect(() => {
    if (openId || !items?.length || typeof window === "undefined" || window.innerWidth < 1024) return;
    const first = items.find((p) => p.status === "ready") ?? items[0];
    onOpen(first.id);
  }, [items, openId, onOpen]);

  const counts = (items ?? []).reduce<Record<string, number>>((acc, p) => {
    acc[p.status] = (acc[p.status] ?? 0) + 1;
    return acc;
  }, {});
  const shown = (items ?? []).filter((p) => filter === "all" || p.status === filter);

  const changed = () => {
    setReload((r) => r + 1);
    onChanged();
  };

  return (
    <div className="flex min-h-0 flex-1">
      <aside className={cn("flex min-h-0 w-full shrink-0 flex-col border-r border-border bg-card lg:w-[360px]", openId && "hidden lg:flex")}>
        <div className="space-y-2.5 border-b border-border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Voorstellen</span>
            <Button variant="ghost" size="icon" onClick={() => setReload((r) => r + 1)} aria-label="Voorstellen vernieuwen" title="Vernieuwen" disabled={loading}>
              <RefreshCw className={cn("h-4 w-4", loading && items && "animate-spin")} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter op status">
            {FILTERS.map((f) => {
              const n = f.value === "all" ? (items?.length ?? 0) : (counts[f.value] ?? 0);
              if (f.value !== "all" && f.value !== "ready" && !n) return null;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  aria-pressed={filter === f.value}
                  className={cn(
                    "h-7 rounded-full border px-2.5 text-xs font-medium transition-colors",
                    filter === f.value ? "border-sl-charcoal bg-sl-charcoal text-white" : "border-border bg-card text-muted-foreground hover:text-foreground",
                  )}
                >
                  {f.label} <span className="tabular-nums opacity-80">{n}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">Publiceren is altijd een menselijke actie, na controle van het bewijs.</p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {error ? (
            <div className="p-4">
              <Alert tone="danger">
                Voorstellen niet geladen: {error}{" "}
                <button type="button" className="font-medium underline" onClick={() => setReload((r) => r + 1)}>
                  Opnieuw
                </button>
              </Alert>
            </div>
          ) : loading && !items ? (
            <div className="space-y-px" aria-hidden>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="h-3.5 w-4/5 animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-muted/70" />
                </div>
              ))}
            </div>
          ) : !shown.length ? (
            <EmptyState
              icon={<FileDiff className="h-5 w-5" />}
              title={filter === "all" ? "Nog geen voorstellen" : filter === "ready" ? "Niets wacht op review" : "Geen voorstellen met deze status"}
              description={filter === "all" || filter === "ready" ? "Vraag de assistent om een kaart te verbeteren; onderbouwde voorstellen verschijnen hier." : undefined}
            />
          ) : (
            <ul className={cn("divide-y divide-border", loading && "opacity-60")}>
              {shown.map((p) => {
                const tables = [...new Set((p.changes ?? []).map((c) => c.fqn.split(".").pop()))];
                const nRepro = p.proof?.filter((x) => x.reproduced).length ?? 0;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(p.id)}
                      aria-current={openId === p.id || undefined}
                      className={cn(
                        "block w-full px-4 py-3 text-left transition-colors hover:bg-sl-grey-50",
                        openId === p.id && "bg-accent shadow-[inset_3px_0_0_var(--color-sl-yellow)] hover:bg-accent",
                      )}
                    >
                      <span className="line-clamp-2 text-sm font-semibold leading-snug">{p.title}</span>
                      <span className="mt-1 block truncate font-mono text-xs text-muted-foreground">{tables.join(", ") || "–"}</span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <StatusBadge status={p.status} />
                        <span>{formatDateTime(p.created_at)}</span>
                        {!!p.proof?.length && (
                          <span className={cn("inline-flex items-center gap-1", nRepro === p.proof.length ? "text-success" : "text-destructive")}>
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {nRepro}/{p.proof.length} bewijs
                          </span>
                        )}
                        <CheckDelta p={p} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
      <div className={cn("flex min-h-0 min-w-0 flex-1 flex-col bg-card", !openId && "hidden lg:flex")}>
        {openId ? (
          <ProposalDetail id={openId} onClose={() => onOpen(null)} onChanged={changed} liveVersion={liveVersion} onOpenTable={onOpenTable} />
        ) : (
          <EmptyState icon={<FileDiff className="h-5 w-5" />} title="Kies een voorstel" description="Selecteer links een voorstel om de wijzigingen en het bewijs te beoordelen." className="flex-1" />
        )}
      </div>
    </div>
  );
}
